import { useAuth0 } from "@auth0/auth0-react";
import {
  useStripe,
  useElements,
  CardCvcElement,
} from "@stripe/react-stripe-js";
import { useState } from "react";
import { useDispatch } from "react-redux";
import { removeSelectedItems } from "store/slices/cartSlice";

import api, { NO_RETRY } from "api/api";
import endpoints from "api/endpoints";
import { Button } from "common";
import ToastNotification from "common/toast/Toast";
import { duplicateOrderMessage, formatCurrency } from "config/config";

const CVC_ELEMENT_OPTIONS = {
  style: {
    base: {
      color: "#424770",
      fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
      fontSmoothing: "antialiased",
      fontSize: "16px",
      "::placeholder": {
        color: "#aab7c4",
      },
    },
    invalid: {
      color: "#9e2146",
      iconColor: "#fa755a",
    },
  },
};

export default function PaymentForm({
  paymentMethod,
  orderTotal,
  customerId,
  onPaymentSuccess,
  onPaymentError,
  buildOrderPayload,
  isProcessing,
  setIsProcessing,
  userInfo,
  billingAddress,
  selectedItems,
}) {
  const stripe = useStripe();
  const elements = useElements();
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();

  const [cvcError, setCvcError] = useState(null);

  // Enhanced error message matching CheckoutReview pattern
  const enhanceErrorMessage = (errorDetail) => {
    if (errorDetail.match(/This record already exists/i)) {
      return duplicateOrderMessage();
    }

    const itemErrorMatch = errorDetail.match(
      /Invalid Field Value (\d+) for the following field: item/i
    );
    if (itemErrorMatch) {
      const invalidItemId = itemErrorMatch[1];
      const invalidItem = selectedItems?.find(
        (item) => String(item.id) === invalidItemId
      );
      if (invalidItem) {
        const itemName = invalidItem.itemid || `Item ${invalidItemId}`;
        return `Cannot process order: "${itemName}" is invalid or no longer available. Please remove it from your cart and try again.`;
      }
      return `Cannot process order: Item with ID ${invalidItemId} is invalid or no longer available. Please review your cart and try again.`;
    }

    return errorDetail;
  };

  // Function to clear cart and localStorage after successful payment
  const clearCheckoutData = () => {
    // Clear cart
    dispatch(removeSelectedItems(userInfo?.id));

    // Clear checkout-related localStorage items
    localStorage.removeItem("selectedPaymentMethodId");
    localStorage.removeItem("paymentMethod");
    localStorage.removeItem("checkoutShippingAddresses");
    localStorage.removeItem("selectedShippingAddressId");
    localStorage.removeItem("checkoutBillingAddresses");
    localStorage.removeItem("selectedBillingAddressId");
    sessionStorage.removeItem("billingSameAsShipping");
    localStorage.removeItem("pendingPaymentIntent");
  };

  if (!paymentMethod) {
    return (
      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <div className="text-center text-gray-500">
          Please select a payment method first
        </div>
      </div>
    );
  }

  const { card, billing_details } = paymentMethod;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    // Validate required data upfront
    // Check if user ID is available
    if (!userInfo?.id) {
      ToastNotification.error(
        "User information not available. Please refresh the page and try again."
      );
      return;
    }

    // Check if billing address is available
    if (!billingAddress) {
      ToastNotification.error(
        "Please select a billing address before placing the order."
      );
      return;
    }

    // Check if cart has items
    if (!selectedItems || selectedItems.length === 0) {
      ToastNotification.error(
        "No items available for checkout. Please select items to order."
      );
      return;
    }

    if (!paymentMethod?.id) {
      ToastNotification.error("Payment method is required.");
      return;
    }

    if (!customerId) {
      ToastNotification.error("Customer ID is required.");
      return;
    }

    if (!orderTotal || orderTotal <= 0) {
      ToastNotification.error("Invalid order total.");
      return;
    }

    setIsProcessing(true);
    setCvcError(null);

    try {
      // Validate CVC
      const { error } = await stripe.createToken(
        "cvc_update",
        elements.getElement(CardCvcElement)
      );

      if (error) {
        setCvcError(error.message);
        setIsProcessing(false);
        return;
      }

      // Build order payload using the standardized function
      const payloadResult = buildOrderPayload ? buildOrderPayload() : null;

      if (!payloadResult) {
        ToastNotification.error(
          "Unable to build order. Please check your information and try again."
        );
        setIsProcessing(false);
        return;
      }

      // Get authentication token
      const token = await getAccessTokenSilently();

      // Call the new single-step payment processing endpoint
      const response = await api.post(
        endpoints.POST_PROCESS_PAYMENT(),
        {
          paymentMethodId: paymentMethod.id,
          amount: orderTotal,
          customerId: customerId,
          description: `Order payment`,
          orderData: payloadResult,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          ...NO_RETRY,
        }
      );

      // Handle the response
      handleServerResponse(response.data);
    } catch (err) {
      const errorDetail =
        err.response?.data?.netsuiteDetail ||
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        "Payment processing failed. Please try again.";

      const enhancedError = enhanceErrorMessage(errorDetail);

      setCvcError(enhancedError);
      setIsProcessing(false);
      ToastNotification.error(enhancedError, { autoClose: false });

      if (onPaymentError) {
        onPaymentError(err);
      }
    }
  };

  // After 3DS auth, call backend to create order + capture payment
  const handleCaptureAfter3DS = async (paymentIntentId) => {
    try {
      const token = await getAccessTokenSilently();
      const payloadResult = buildOrderPayload ? buildOrderPayload() : null;

      const response = await api.post(
        endpoints.POST_CAPTURE_PAYMENT(),
        {
          paymentIntentId,
          orderData: payloadResult,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          ...NO_RETRY,
        }
      );

      handleServerResponse(response.data);
    } catch (err) {
      const errorDetail =
        err.response?.data?.netsuiteDetail ||
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        "Payment capture failed. Please try again.";

      const enhancedError = enhanceErrorMessage(errorDetail);

      setCvcError(enhancedError);
      setIsProcessing(false);
      ToastNotification.error(enhancedError, { autoClose: false });

      if (onPaymentError) {
        onPaymentError(err);
      }
    }
  };

  function handleServerResponse(response) {
    const paymentIntent = response.paymentIntent || response;

    // Check for duplicate order response
    if (response.isDuplicate === true) {
      const errorMsg = response.message || duplicateOrderMessage();
      setCvcError(errorMsg);
      setIsProcessing(false);
      ToastNotification.error(errorMsg, { autoClose: false });

      if (onPaymentError) {
        onPaymentError(new Error(errorMsg));
      }
      return;
    }

    if (paymentIntent.error) {
      const errorMsg = paymentIntent.error.message || "Payment failed";
      setCvcError(errorMsg);
      setIsProcessing(false);
      ToastNotification.error(errorMsg, { autoClose: false });

      if (onPaymentError) {
        onPaymentError(paymentIntent.error);
      }
    } else if (response.requiresAction && paymentIntent.next_action) {
      // 3D Secure required — no order created yet, handle auth then capture
      handleAction(paymentIntent);
    } else if (paymentIntent.status === "succeeded") {
      // Payment captured and order created successfully
      clearCheckoutData();

      // orderFinalized is false when the charge went through but NetSuite
      // rejected the release PATCH (order still Pending Approval, no email);
      // ops get an audit record, the customer gets softer copy.
      ToastNotification.success(
        response.orderFinalized === false
          ? "Payment received. Your order is being finalized and the confirmation email may take a little longer than usual."
          : "Payment and order completed successfully! You'll receive a confirmation email shortly."
      );

      setIsProcessing(false);
      if (onPaymentSuccess) {
        onPaymentSuccess(paymentIntent);
      }
    } else {
      const errorMsg = `Payment status: ${paymentIntent.status}. Please try again.`;
      setCvcError(errorMsg);
      setIsProcessing(false);
      ToastNotification.error(errorMsg, { autoClose: false });
    }
  }
  function handleAction(response) {
    // The backend creates the PaymentIntent with confirm:true and Stripe's
    // default (automatic) confirmation, so Stripe.js must finish the 3DS
    // challenge with handleNextAction. handleCardAction only accepts intents
    // created with confirmation_method:"manual" and throws
    // "does not require manual server-side confirmation" otherwise.
    stripe
      .handleNextAction({ clientSecret: response.client_secret })
      .then(function (result) {
        if (result.error) {
          setCvcError(result.error.message || "Authentication failed");
          setIsProcessing(false);
          ToastNotification.error(
            result.error.message || "Authentication failed. Please try again."
          );

          if (onPaymentError) {
            onPaymentError(result.error);
          }
        } else {
          // 3DS authentication succeeded
          // With capture_method: "manual", the PI is now in requires_capture state
          // Call backend to create order + capture payment
          setCvcError(null);

          if (
            result.paymentIntent?.status === "requires_capture" ||
            result.paymentIntent?.status === "succeeded"
          ) {
            handleCaptureAfter3DS(result.paymentIntent.id);
          } else {
            setCvcError(
              "Payment authentication completed but authorization failed"
            );
            setIsProcessing(false);
            ToastNotification.error(
              "Payment authorization failed. Please try again."
            );
          }
        }
      })
      .catch(function (err) {
        // Stripe.js rejects (network drop, IntegrationError, popup blocked)
        // rather than resolving with { error }. Without this the button stayed
        // in "processing" forever with no message. No order exists yet at this
        // point; the authorization hold expires on its own.
        const errorMsg =
          err?.message ||
          "Authentication could not be completed. Please try again.";
        setCvcError(errorMsg);
        setIsProcessing(false);
        ToastNotification.error(errorMsg, { autoClose: false });

        if (onPaymentError) {
          onPaymentError(err);
        }
      });
  }

  const formatCardBrand = (brand) => {
    const brandMap = {
      visa: "Visa",
      mastercard: "Mastercard",
      amex: "American Express",
      discover: "Discover",
      diners: "Diners Club",
      jcb: "JCB",
      unionpay: "UnionPay",
    };
    return brandMap[brand] || brand;
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-sm border">
      <h3 className="text-lg font-semibold mb-4">Complete Payment</h3>

      {/* Selected Payment Method Display */}
      <div className="mb-6 p-4 bg-gray-50 rounded-lg">
        <h4 className="font-medium mb-2">Selected Payment Method</h4>
        <div className="flex items-center space-x-3">
          <div className="w-8 h-6 bg-gray-200 rounded flex items-center justify-center text-xs font-semibold">
            {card.brand.toUpperCase()}
          </div>
          <div>
            <div className="font-medium">
              {formatCardBrand(card.brand)} •••• {card.last4}
            </div>
            <div className="text-sm text-gray-500">
              Expires {card.exp_month.toString().padStart(2, "0")}/
              {card.exp_year}
            </div>
            {billing_details?.name && (
              <div className="text-sm text-gray-500">
                {billing_details.name}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Payment Amount */}
      <div className="mb-6 p-4 bg-blue-50 rounded-lg">
        <div className="flex justify-between items-center">
          <span className="font-medium">Total Amount:</span>
          <span className="text-xl font-bold text-blue-600">
            {formatCurrency(orderTotal)} CAD
          </span>
        </div>
      </div>

      {/* CVC Form */}
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Enter CVC/CVV for verification *
          </label>
          <div className="border border-gray-300 rounded-md p-3 focus-within:ring-2 focus-within:ring-blue-500">
            <CardCvcElement
              options={CVC_ELEMENT_OPTIONS}
              onChange={() => {
                setCvcError(null);
              }}
            />
          </div>
          {cvcError && <p className="text-red-600 text-sm mt-1">{cvcError}</p>}
        </div>

        <div className="text-xs text-gray-500 mb-4">
          <p>
            • Your CVC is the 3-digit security code on the back of your card
          </p>
          <p>• This helps verify that you have the physical card</p>
        </div>

        <Button
          type="submit"
          disabled={!stripe || isProcessing}
          className="w-full py-3 text-lg"
        >
          {isProcessing
            ? "Processing Payment..."
            : `Pay ${formatCurrency(orderTotal)}`}
        </Button>
      </form>

      {/* Security Notice */}
      <div className="mt-4 text-xs text-gray-500 text-center">
        <p>🔒 Your payment information is secure and encrypted</p>
        <p>Powered by Stripe</p>
      </div>
    </div>
  );
}
